import React, { useState, useRef, useCallback } from 'react';
import { Upload, Copy, Check, FileText, Loader2, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CorporateOnboardingWizard = ({ company, accessCodes, getToken, onComplete, onCompanyUpdate, onEmployeesUpdate }) => {
  const [step, setStep] = useState(1);
  const [closing, setClosing] = useState(false);
  const [celebrateMsg, setCelebrateMsg] = useState(false);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoSkipped, setLogoSkipped] = useState(false);
  const logoRef = useRef(null);

  const [codeCopied, setCodeCopied] = useState(false);

  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvRef = useRef(null);

  const skippedSteps = [];
  const activeCode = accessCodes?.find(c => c.active);

  const handleLogoDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('File exceeds 2MB limit'); return; }
    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) { alert('Only PNG, JPG, or SVG allowed'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }, []);

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('File exceeds 2MB limit'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', logoFile);
      const res = await fetch(`${API_URL}/api/corporate/upload-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        onCompanyUpdate(prev => ({ ...prev, logo_url: data.logo_url }));
        setStep(2);
      } else {
        alert(data.detail || 'Upload failed');
      }
    } catch { alert('Upload failed'); }
    setLogoUploading(false);
  };

  const continueStep1 = async () => {
    if (logoFile) {
      await uploadLogo();
    } else if (logoSkipped || company?.logo_url) {
      setStep(2);
    }
  };

  const copyCode = () => {
    if (!activeCode?.code) return;
    navigator.clipboard.writeText(activeCode.code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/corporate/employees/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      setCsvResult(data);
      if (data.added > 0 && onEmployeesUpdate) onEmployeesUpdate();
    } catch { setCsvResult({ error: 'Upload failed' }); }
    setCsvUploading(false);
    if (csvRef.current) csvRef.current.value = '';
  };

  const finishWizard = async (skippedCsv) => {
    const finalSkipped = [...skippedSteps];
    if (logoSkipped && !company?.logo_url) finalSkipped.push('logo');
    if (skippedCsv) finalSkipped.push('employees');
    try {
      await fetch(`${API_URL}/api/corporate/wizard/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ skipped_steps: finalSkipped }),
      });
    } catch {}
    setCelebrateMsg(true);
    setTimeout(() => {
      setClosing(true);
      setTimeout(() => onComplete(finalSkipped), 400);
    }, 2000);
  };

  if (celebrateMsg) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-400 ${closing ? 'opacity-0' : 'opacity-100'}`}
        style={{ backgroundColor: 'rgba(26,18,9,0.85)' }}>
        <div className="text-center" data-testid="wizard-celebrate">
          <h1 className="text-2xl font-bold text-white mb-2">You&apos;re live. Welcome to HiBFF for Business.</h1>
          <div className="w-12 h-1 rounded mx-auto mt-4" style={{ backgroundColor: '#F5831F' }} />
        </div>
      </div>
    );
  }

  const step1Ready = !!logoFile || logoSkipped || !!company?.logo_url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(26,18,9,0.75)' }}
      data-testid="corporate-onboarding-wizard-overlay">
      <div className="bg-white rounded-2xl w-full shadow-2xl overflow-hidden" style={{ maxWidth: 560 }} data-testid="corporate-onboarding-wizard-card">
        {/* Progress */}
        <div className="flex items-center gap-0 px-8 pt-6 pb-4" data-testid="wizard-progress">
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                  style={s === step ? { backgroundColor: '#F5831F', color: 'white' } : s < step ? { backgroundColor: '#2EAA5C', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#999' }}
                  data-testid={`wizard-step-${s}-indicator`}>
                  {s < step ? <Check size={14} /> : s}
                </div>
                <span className="text-xs font-medium hidden sm:inline" style={{ color: s === step ? '#1A1209' : '#999' }}>
                  {s === 1 ? 'Logo' : s === 2 ? 'Code' : 'Team'}
                </span>
              </div>
              {s < 3 && <div className="flex-1 h-px mx-2" style={{ backgroundColor: s < step ? '#2EAA5C' : '#E8E8E8' }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="px-8 pb-8">
          {step === 1 && (
            <div data-testid="wizard-step-1">
              <h2 className="text-lg font-bold mb-1" style={{ color: '#1A1209' }}>Brand it.</h2>
              <p className="text-sm mb-5" style={{ color: '#888' }}>
                Add your company logo — it appears on your employees&apos; dashboard alongside the HiBFF logo.
              </p>
              <div
                onDrop={handleLogoDrop}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={() => logoRef.current?.click()}
                className="border-2 border-dashed rounded-xl p-8 mb-4 cursor-pointer text-center transition-colors hover:border-orange-300"
                style={{ borderColor: logoPreview || company?.logo_url ? '#2EAA5C' : '#E0E0E0', backgroundColor: '#FAFAF8' }}
                data-testid="wizard-logo-dropzone">
                <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.svg" className="hidden" onChange={handleLogoSelect} data-testid="wizard-logo-input" />
                {logoPreview ? (
                  <img src={logoPreview} alt="Preview" className="mx-auto mb-2" style={{ height: 48, maxWidth: 160, objectFit: 'contain' }} />
                ) : company?.logo_url ? (
                  <img src={`${API_URL}${company.logo_url}`} alt="Current logo" className="mx-auto mb-2" style={{ height: 48, maxWidth: 160, objectFit: 'contain' }} />
                ) : (
                  <Upload size={28} className="mx-auto mb-2" style={{ color: '#CCC' }} />
                )}
                <p className="text-xs" style={{ color: '#999' }}>{logoPreview ? 'Click to change' : 'Drag & drop or click to browse'}</p>
                <p className="text-[10px] mt-1" style={{ color: '#CCC' }}>PNG, JPG, or SVG — max 2MB</p>
              </div>
              {(logoPreview || company?.logo_url) && (
                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: '#1A1209' }} data-testid="wizard-logo-preview">
                  <p className="text-[10px] font-medium mb-3 uppercase tracking-wider" style={{ color: '#A89880' }}>How it will look in the employee dashboard</p>
                  <div className="flex items-center gap-3">
                    <img src={logoPreview || `${API_URL}${company.logo_url}`} alt="Company logo" style={{ height: 40, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
                    <div style={{ width: 1, height: 32, backgroundColor: '#555' }} />
                    <img src="/hibff-logo.png" alt="HiBFF" style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
                  </div>
                </div>
              )}
              {!logoPreview && !company?.logo_url && (
                <button onClick={() => setLogoSkipped(true)} className="text-xs underline mb-4 block mx-auto" style={{ color: '#999' }} data-testid="wizard-skip-logo">
                  Skip for now
                </button>
              )}
              <button onClick={continueStep1} disabled={!step1Ready || logoUploading}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-40"
                style={{ backgroundColor: '#F5831F' }} data-testid="wizard-step1-continue">
                {logoUploading ? <><Loader2 size={14} className="animate-spin" /> Uploading...</> : <>Continue <ChevronRight size={14} /></>}
              </button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="wizard-step-2">
              <h2 className="text-lg font-bold mb-1" style={{ color: '#1A1209' }}>Invite your team.</h2>
              <p className="text-sm mb-5" style={{ color: '#888' }}>
                Share this enrollment code with employees so they can register at hibff.com/join.
              </p>
              <div className="rounded-xl p-6 mb-4 text-center" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FDBA7433' }} data-testid="wizard-code-display">
                <p className="font-mono font-bold tracking-[0.2em]" style={{ fontSize: 24, color: '#1A1209' }} data-testid="wizard-code-value">
                  {activeCode?.code || '—'}
                </p>
              </div>
              <button onClick={copyCode}
                className="w-full py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 mb-5 transition-all"
                style={codeCopied ? { backgroundColor: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0' } : { backgroundColor: '#1A1209', color: 'white' }}
                data-testid="wizard-copy-code">
                {codeCopied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Code</>}
              </button>
              <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: '#F8F7F4' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: '#1A1209' }}>Three ways to share:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-xs" style={{ color: '#666' }}>
                    <span className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold mt-0.5" style={{ backgroundColor: '#F5831F22', color: '#F5831F' }}>1</span>
                    Add it to your company intranet or Slack/Teams announcement
                  </li>
                  <li className="flex items-start gap-2 text-xs" style={{ color: '#666' }}>
                    <span className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold mt-0.5" style={{ backgroundColor: '#F5831F22', color: '#F5831F' }}>2</span>
                    Include it in your next company-wide email or newsletter
                  </li>
                  <li className="flex items-start gap-2 text-xs" style={{ color: '#666' }}>
                    <span className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold mt-0.5" style={{ backgroundColor: '#F5831F22', color: '#F5831F' }}>3</span>
                    Share directly with team leads to distribute within their departments
                  </li>
                </ul>
              </div>
              <button onClick={() => setStep(3)}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                style={{ backgroundColor: '#F5831F' }} data-testid="wizard-step2-continue">
                Continue <ChevronRight size={14} />
              </button>
            </div>
          )}

          {step === 3 && (
            <div data-testid="wizard-step-3">
              <h2 className="text-lg font-bold mb-1" style={{ color: '#1A1209' }}>Nearly there.</h2>
              <p className="text-sm mb-5" style={{ color: '#888' }}>
                Upload your employee roster now or come back to this any time from your Team tab.
              </p>
              {csvResult && (
                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: csvResult.error ? '#FEF2F2' : '#ECFDF5', border: `1px solid ${csvResult.error ? '#FECACA' : '#A7F3D0'}` }} data-testid="wizard-csv-result">
                  {csvResult.error ? (
                    <p className="text-sm text-red-700">{csvResult.error}</p>
                  ) : (
                    <p className="text-sm font-semibold" style={{ color: '#059669' }}>
                      <Check size={14} className="inline mr-1" />
                      {csvResult.added} employee{csvResult.added !== 1 ? 's' : ''} added successfully
                    </p>
                  )}
                </div>
              )}
              {csvResult?.added > 0 ? (
                <button onClick={() => finishWizard(false)}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#2EAA5C' }} data-testid="wizard-finish-btn">
                  <Check size={14} /> Finish Setup
                </button>
              ) : (
                <div className="flex gap-3">
                  <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} data-testid="wizard-csv-input" />
                  <button onClick={() => csvRef.current?.click()} disabled={csvUploading}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: '#F5831F' }} data-testid="wizard-upload-csv">
                    {csvUploading ? <><Loader2 size={14} className="animate-spin" /> Uploading...</> : <><FileText size={14} /> Upload CSV Now</>}
                  </button>
                  <button onClick={() => finishWizard(true)}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ border: '1px solid #E0E0E0', color: '#666' }} data-testid="wizard-skip-csv">
                    I&apos;ll do this later
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateOnboardingWizard;
